import React, { useMemo } from 'react';
import CreatableSelect from 'react-select/creatable';
import { MOTOR_TYPES, DIRECTION_TYPES } from '../../../../constants';
import { formatPrice, getStorZebraExtraPrice, resolveStorWidthLimitCm } from '../../../../utils';
import { handleNumericKeyDown, customSelectStyles } from '../OrderFormUtils';

const StorZebraForm = ({
  newOrder,
  onFieldChange,
  isLoading,
  productItems,
  productOptions,
  handleProductSelect,
  adminSettings,
  selectedProductType,
  handleMotorTypeChange
}) => {
  const extraPrice = (key) => getStorZebraExtraPrice(adminSettings, selectedProductType, key);
  const isStor = selectedProductType === 'stor';

  const widthLimitCm = useMemo(
    () => (isStor ? resolveStorWidthLimitCm(newOrder, productItems) : null),
    [isStor, newOrder, productItems]
  );

  const isWidthOverLimit = widthLimitCm && parseFloat(newOrder.width) > widthLimitCm;

  const handleWidthChange = (rawValue) => {
    if (!isStor || !widthLimitCm) {
      onFieldChange('width', rawValue);
      return;
    }

    const parsed = parseFloat(rawValue);
    if (rawValue !== '' && !isNaN(parsed) && parsed > widthLimitCm) {
      onFieldChange('width', String(widthLimitCm));
      return;
    }
    onFieldChange('width', rawValue);
  };

  const handleProductChange = (option) => {
    if (!option) {
      onFieldChange('productName', '');
      onFieldChange('unitPrice', '');
      onFieldChange('productItemId', '');
      onFieldChange('productMaxWidth200cm', false);
    } else if (option.__isNew__) {
      onFieldChange('productName', option.value);
      onFieldChange('unitPrice', '');
      onFieldChange('productItemId', '');
      onFieldChange('productMaxWidth200cm', false);
    } else {
      const product = productItems.find((p) => p.id === option.value);
      handleProductSelect(option.value);
      onFieldChange('productItemId', option.value);
      onFieldChange('productMaxWidth200cm', !!(product && product.maxWidth200cm));
    }
  };

  return (
    <>
      <div className="form-group">
        <label>Oda Adı *</label>
        <input
          type="text"
          value={newOrder.roomName || ''}
          onChange={(e) => onFieldChange('roomName', e.target.value)}
          required
          disabled={isLoading}
        />
      </div>

      <div className="form-group">
        <label>Ürün Adı *</label>
        <CreatableSelect
          options={productOptions}
          value={
            productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
            (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
          }
          onChange={handleProductChange}
          isDisabled={isLoading}
          isClearable
          isSearchable
          placeholder="Ürün seçin veya yazın..."
          noOptionsMessage={() => "Ürün bulunamadı"}
          formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`}
          styles={customSelectStyles}
        />
      </div>

      <div className="form-group">
        <label>Ürün Varyantı</label>
        <input
          type="text"
          value={newOrder.productVariant || ''}
          onChange={(e) => onFieldChange('productVariant', e.target.value)}
          disabled={isLoading}
          placeholder="Renk, desen vb. giriniz..."
        />
      </div>

      <div className="form-group">
        <label>En (cm) *</label>
        <input
          type="number"
          step="0.01"
          min="0"
          max={widthLimitCm || undefined}
          value={newOrder.width || ''}
          onKeyDown={handleNumericKeyDown}
          onChange={(e) => handleWidthChange(e.target.value)}
          required
          disabled={isLoading}
          className={isWidthOverLimit ? 'input-warning' : ''}
        />
        {widthLimitCm && (
          <small className={`field-hint ${isWidthOverLimit ? 'field-hint-warning' : ''}`}>
            {isWidthOverLimit
              ? `Bu ürün için maksimum en ${widthLimitCm} cm ile sınırlıdır.`
              : `Bu ürün için en tavanı: ${widthLimitCm} cm`}
          </small>
        )}
      </div>

      <div className="form-group">
        <label>Boy (cm) *</label>
        <input
          type="number"
          step="0.01"
          value={newOrder.height || ''}
          onKeyDown={handleNumericKeyDown}
          onChange={(e) => onFieldChange('height', e.target.value)}
          required
          disabled={isLoading}
        />
      </div>

      {isStor && (
        <div className="form-group">
          <label>Fire İptal</label>
          <label className="stor-inline-checkbox-label">
            <input
              type="checkbox"
              checked={!!newOrder.storFireIptal}
              onChange={(e) => onFieldChange('storFireIptal', e.target.checked)}
              disabled={isLoading}
            />
            <span>141–200 cm aralığında 2 m yerine normal yuvarlama</span>
          </label>
        </div>
      )}

      {newOrder.width && newOrder.usedM2 && (
        <div className="form-group">
          <label>Kullanılan Mt²</label>
          <input
            type="text"
            value={newOrder.usedM2}
            readOnly
            className="readonly-field"
          />
        </div>
      )}

      <div className="form-group">
        <label>Yön *</label>
        <select
          value={newOrder.direction || ''}
          onChange={(e) => onFieldChange('direction', e.target.value)}
          required
          disabled={isLoading}
        >
          <option value="">Seçiniz</option>
          {Object.entries(DIRECTION_TYPES).map(([key, value]) => (
            <option key={key} value={value}>{value}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Birim Fiyat (₺) *</label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={newOrder.unitPrice || ''}
          onKeyDown={handleNumericKeyDown}
          onChange={(e) => onFieldChange('unitPrice', e.target.value)}
          required
          disabled={isLoading}
        />
      </div>

      <div className="form-group">
        <label>Motor</label>
        <select
          value={newOrder.motorType || ''}
          onChange={handleMotorTypeChange}
          disabled={isLoading}
        >
          <option value="">Seçiniz</option>
          {Object.entries(MOTOR_TYPES).map(([key, value]) => (
            <option key={key} value={value}>{value}</option>
          ))}
        </select>
      </div>

      {newOrder.motorType && (
        <div className="form-group">
          <label>Motor Ücreti ({adminSettings[`${selectedProductType}MotorCurrency`] || 'USD'})</label>
          <input
            type="number"
            step="0.01"
            value={newOrder.motorPriceCurrency || ''}
            onChange={(e) => onFieldChange('motorPriceCurrency', e.target.value)}
            disabled={isLoading}
          />
          {newOrder.motorPriceCurrency && (
            <small style={{ display: 'block', marginTop: '5px', color: '#b04309' }}>
              ≈ {newOrder.motorPrice ? Number(newOrder.motorPrice).toFixed(2) : 0} ₺
            </small>
          )}
        </div>
      )}

      <div className="form-group">
        <label>Montaj (₺)</label>
        <input
          type="number"
          step="0.01"
          value={newOrder.mounting || ''}
          onChange={(e) => onFieldChange('mounting', e.target.value)}
          disabled={isLoading}
        />
      </div>

      <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginTop: '15px', marginBottom: '15px' }}>
        {isStor && (
          <>
            <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#b04309' }} checked={newOrder.hasStorJumbo || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorJumbo', checked); onFieldChange('storJumboAmount', checked ? extraPrice('jumbo') : 0); }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Jumbo Sistem</span>
                  <small style={{ color: '#b04309', fontSize: '11px' }}>{extraPrice('jumbo')} ₺</small>
                </div>
              </label>
            </div>
            <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#b04309' }} checked={newOrder.hasStorAltEtekDilim || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorAltEtekDilim', checked); onFieldChange('storAltEtekDilimAmount', checked ? extraPrice('altEtekDilim') : 0); }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Alt Etek Dilim</span>
                  <small style={{ color: '#b04309', fontSize: '11px' }}>{extraPrice('altEtekDilim')} ₺</small>
                </div>
              </label>
            </div>
            <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#b04309' }} checked={newOrder.hasStorKapaliKasa || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorKapaliKasa', checked); onFieldChange('storKapaliKasaAmount', checked ? extraPrice('kapaliKasa') : 0); }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Kapalı Kasa</span>
                  <small style={{ color: '#b04309', fontSize: '11px' }}>{extraPrice('kapaliKasa')} ₺</small>
                </div>
              </label>
            </div>
            <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#b04309' }} checked={newOrder.hasStorAltEtekLazer || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorAltEtekLazer', checked); onFieldChange('storAltEtekLazerAmount', checked ? extraPrice('altEtekLazer') : 0); }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Alt Etek Lazer</span>
                  <small style={{ color: '#b04309', fontSize: '11px' }}>{extraPrice('altEtekLazer')} ₺</small>
                </div>
              </label>
            </div>
            <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#b04309' }} checked={newOrder.hasStorDekoratifLama || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorDekoratifLama', checked); onFieldChange('storDekoratifLamaAmount', checked ? extraPrice('dekoratifLama') : 0); }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Dekoratif Lama</span>
                  <small style={{ color: '#b04309', fontSize: '11px' }}>{extraPrice('dekoratifLama')} ₺</small>
                </div>
              </label>
            </div>
            <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#b04309' }} checked={newOrder.hasStorDekoratifKasa || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorDekoratifKasa', checked); onFieldChange('storDekoratifKasaAmount', checked ? extraPrice('dekoratifKasa') : 0); }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Dekoratif Kasa</span>
                  <small style={{ color: '#b04309', fontSize: '11px' }}>{extraPrice('dekoratifKasa')} ₺</small>
                </div>
              </label>
            </div>
            <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#b04309' }} checked={newOrder.hasStorAltEtekBeyazIs || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorAltEtekBeyazIs', checked); onFieldChange('storAltEtekBeyazIsAmount', checked ? extraPrice('altEtekBeyazIs') : 0); }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Alt Etek Beyaz İş</span>
                  <small style={{ color: '#b04309', fontSize: '11px' }}>{extraPrice('altEtekBeyazIs')} ₺</small>
                </div>
              </label>
            </div>
            <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#b04309' }} checked={newOrder.hasStorBoru47 || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorBoru47', checked); onFieldChange('storBoru47Amount', checked ? extraPrice('boru47') : 0); }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>47&apos;lik Boru</span>
                  <small style={{ color: '#b04309', fontSize: '11px' }}>{extraPrice('boru47')} ₺</small>
                </div>
              </label>
            </div>
            <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#b04309' }} checked={newOrder.hasStorMekanizma47 || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorMekanizma47', checked); onFieldChange('storMekanizma47Amount', checked ? extraPrice('mekanizma47') : 0); }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>47&apos;lik Mekanizma</span>
                  <small style={{ color: '#b04309', fontSize: '11px' }}>{extraPrice('mekanizma47')} ₺</small>
                </div>
              </label>
            </div>
          </>
        )}

        {selectedProductType === 'zebra' && (
          <>
            <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#b04309' }} checked={newOrder.hasZebraJumbo || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasZebraJumbo', checked); onFieldChange('zebraJumboAmount', checked ? extraPrice('jumbo') : 0); }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Jumbo Sistem</span>
                  <small style={{ color: '#b04309', fontSize: '11px' }}>{extraPrice('jumbo')} ₺</small>
                </div>
              </label>
            </div>
            <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#b04309' }} checked={newOrder.hasZebraAltEtekDilim || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasZebraAltEtekDilim', checked); onFieldChange('zebraAltEtekDilimAmount', checked ? extraPrice('altEtekDilim') : 0); }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Alt Etek Dilim</span>
                  <small style={{ color: '#b04309', fontSize: '11px' }}>{extraPrice('altEtekDilim')} ₺</small>
                </div>
              </label>
            </div>
            <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }}>
                <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#b04309' }} checked={newOrder.hasZebraKapaliKasa || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasZebraKapaliKasa', checked); onFieldChange('zebraKapaliKasaAmount', checked ? extraPrice('kapaliKasa') : 0); }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155' }}>Kapalı Kasa</span>
                  <small style={{ color: '#b04309', fontSize: '11px' }}>{extraPrice('kapaliKasa')} ₺</small>
                </div>
              </label>
            </div>
          </>
        )}
      </div>

      <div className="form-group">
        <label>Adet *</label>
        <input
          type="number"
          min="1"
          value={newOrder.quantity || ''}
          onKeyDown={handleNumericKeyDown}
          onChange={(e) => onFieldChange('quantity', e.target.value)}
          required
          disabled={isLoading}
          placeholder="Adet giriniz"
        />
      </div>

      <div className="form-group">
        <label>İskonto (%)</label>
        <input
          type="number"
          min="0"
          max="100"
          value={newOrder.discount || ''}
          onKeyDown={handleNumericKeyDown}
          onChange={(e) => onFieldChange('discount', e.target.value)}
          disabled={isLoading}
        />
      </div>

      {newOrder.productName && (
        <div className="form-group">
          <label>Toplam Tutar (₺)</label>
          <input
            type="text"
            value={formatPrice(newOrder.totalPrice)}
            readOnly
            className="readonly-field total-price"
          />
        </div>
      )}

      {newOrder.discount > 0 && (
        <div className="form-group">
          <label>İndirimli Tutar (₺)</label>
          <input
            type="text"
            value={formatPrice(newOrder.discountedPrice)}
            readOnly
            className="readonly-field discounted-price"
          />
        </div>
      )}
    </>
  );
};

export default StorZebraForm;
